import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  PlusSubscriptionModalInfoActionType,
  PlusSubscriptionModalPayloadActionType,
} from 'src/models/store/plusSubscription';
import { PlusSubscriptionModalInfoResponseType } from 'src/models/plusSubscription';
import { BaseApiErrorType } from 'src/models/BaseApi';
import { BaseStoreEntityType, baseStoreEntityValue } from 'src/store/types';
import { SafeAnyType } from 'src/utils/safeAny';

export type PlusSubscriptionSliceStateType = {
  modal: {
    type: 'planSelection' | 'trialExpired' | 'upgradeNow' | 'paymentFailed' | 'unlimited' | 'planPreselected' | null;
    canClose: boolean;
    closeCallback?: () => void;
    additionalParams?: SafeAnyType;
  };
  modalInfo: BaseStoreEntityType<PlusSubscriptionModalInfoResponseType>;
};

const initialPlanSelectionModalInfo: PlusSubscriptionModalInfoResponseType['PlanSelectionModalInfo'] = {
  IsFreeTrialExpired: false,
  Plans: [],
  PlusSubscriptionCustomerInfo: {
    FirstName: '',
    LastName: '',
    Email: '',
    CompanyName: '',
  },
  ComparePricingLink: '',
};

const initialFailedPaymentModalInfo = {
  IsGracePeriodExpired: false,
  ChargebeeSelfServePortalUrl: '',
  FailedPaymentReason: '',
  GracePeriodExpirationDate: null,
};

const initialState: PlusSubscriptionSliceStateType = {
  modal: {
    type: null,
    canClose: false,
  },
  modalInfo: baseStoreEntityValue<PlusSubscriptionModalInfoResponseType>(
    {
      PlusSubscriptionModalType: null,
      IsPlusSubscriptionAvailable: false,
      IsFreeTrialAvailable: false,
      IsRallyUpPlusFeatureFlagEnabled: false,
      PlanSelectionModalInfo: initialPlanSelectionModalInfo,
      FailedPaymentModalInfo: initialFailedPaymentModalInfo,
      PlanPreselectedModalInfo: null,
      IsSubscriptionDialogHeaderVisible: true,
    },
    true
  ),
};

export const plusSubscriptionSlice = createSlice({
  name: 'plusSubscription',
  initialState,
  reducers: {
    showModal: (state, { payload }: PlusSubscriptionModalPayloadActionType) => {
      state.modal.type = payload.type;
      state.modal.canClose = !!payload.canClose;
      state.modal.closeCallback = payload.closeCallback;
      state.modal.additionalParams = payload.additionalParams;
    },
    closeModal: (state) => {
      state.modal = {
        type: null,
        canClose: false,
        closeCallback: undefined,
        additionalParams: undefined,
      };
    },

    getModalInfoStart: (state) => {
      state.modalInfo.loading = true;
    },
    getModalInfoOk: (state, { payload }: PlusSubscriptionModalInfoActionType) => {
      state.modalInfo.response = {
        PlusSubscriptionModalType: payload.PlusSubscriptionModalType,
        IsPlusSubscriptionAvailable: payload.IsPlusSubscriptionAvailable,
        IsFreeTrialAvailable: payload.IsFreeTrialAvailable,
        IsRallyUpPlusFeatureFlagEnabled: payload.IsRallyUpPlusFeatureFlagEnabled,
        PlanSelectionModalInfo: payload.PlanSelectionModalInfo
          ? payload.PlanSelectionModalInfo
          : initialPlanSelectionModalInfo,
        FailedPaymentModalInfo: payload.FailedPaymentModalInfo
          ? payload.FailedPaymentModalInfo
          : initialFailedPaymentModalInfo,
        PlanPreselectedModalInfo: payload.PlanPreselectedModalInfo,
        IsSubscriptionDialogHeaderVisible: payload.IsSubscriptionDialogHeaderVisible,
      };
      state.modalInfo.loading = false;
    },
    getModalInfoError: (state, { payload }: PayloadAction<BaseApiErrorType>) => {
      state.modalInfo.error = payload;
      state.modalInfo.loading = false;
    },
  },
});
